"use client";

// ============================================================
// CLUBHUB – Zuordnung anlegen und bearbeiten (ContactFee)
// ============================================================
// Wer zahlt was, ab wann, wie. Ein abweichender Betrag braucht
// einen Grund — er steht auf der Rechnung und im Protokoll. Wer
// per Lastschrift zahlen soll, braucht ein gültiges Mandat; fehlt
// es, sagt der Dialog das, bevor der Lauf es tut.
//
// Mit Sitzung speichert `zuordnungSpeichern` (Historie statt
// Überschreiben: ein Wechsel beendet die bisherige Zuordnung am
// Vortag). Ohne Sitzung rechnet der Dialog wie bisher selbst.
// ============================================================
import { useState } from "react";
import Link from "next/link";
import {
  Dialog,
  DialogCancel,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogForm,
  DialogHeader,
  DialogNotice,
  DialogRow,
  DialogSection,
  DialogSubmit,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { AmountInput } from "@/components/ui/amount-input";
import { FieldLabel } from "@/components/ui/field-label";
import { Switch } from "@/components/ui/switch";
import { KontaktAuswahl } from "@/components/mitglieder/kontakt-auswahl";
import { toast } from "@/components/ui/toast";
import { MOCK_CONTACTS, MOCK_HOUSEHOLDS } from "@/lib/mock/core";
import { zuordnungSpeichern } from "@/app/(app)/beitraege/actions";
import { FEE_INTERVAL } from "@/lib/labels";
import { euro, heuteIso } from "@/lib/rechnungen/format";
import type { BeitragsartView, MandatsLage, ZuordnungView } from "@/lib/beitraege/views";
import type { PaymentMethod } from "@/generated/prisma/enums";
import type { ContactView, HouseholdView } from "@/lib/views";

let neueNummer = 0;

export type MandatInfo = { id: string; reference: string; ibanMasked: string; lage: MandatsLage };

export function ZuordnungDialog({
  zuordnung,
  arten,
  mandate,
  kontakte,
  haushalte,
  datenbank = false,
  onClose,
  onGespeichert,
}: {
  zuordnung: ZuordnungView | null;
  arten: BeitragsartView[];
  mandate: Record<string, MandatInfo>;
  /** Personen und Familien des Vereins — ohne Angabe der Musterbestand. */
  kontakte?: ContactView[];
  haushalte?: HouseholdView[];
  datenbank?: boolean;
  onClose: () => void;
  onGespeichert: (z: ZuordnungView) => void;
}) {
  const [kontaktId, setKontaktId] = useState<string | null>(zuordnung?.contactId ?? null);
  const [artId, setArtId] = useState(zuordnung?.feeTypeId ?? arten.find((a) => a.isActive)?.id ?? "");
  const [zahlart, setZahlart] = useState<PaymentMethod>(zuordnung?.paymentMethod ?? "SEPA");
  const [abweichend, setAbweichend] = useState(zuordnung?.amountOverride !== null && zuordnung !== null);
  const [fehler, setFehler] = useState<Record<string, string>>({});

  const personen = kontakte ?? MOCK_CONTACTS;
  const familien = haushalte ?? MOCK_HOUSEHOLDS;
  const kontakt = personen.find((c) => c.id === kontaktId);
  const haushalt = kontakt ? familien.find((h) => h.contactIds.includes(kontakt.id)) : undefined;
  const zahler = (haushalt?.billingContactId ? personen.find((c) => c.id === haushalt.billingContactId) : undefined) ?? kontakt;
  const art = arten.find((a) => a.id === artId);
  const mandat = zahler ? mandate[zahler.id] : undefined;
  const lage: MandatsLage = mandat?.lage ?? "fehlt";
  const wechsel = datenbank && zuordnung !== null && (artId !== zuordnung.feeTypeId || abweichend !== (zuordnung.amountOverride !== null));

  async function speichern(fd: FormData) {
    const f: Record<string, string> = {};
    const von = String(fd.get("von") ?? "");
    const bis = String(fd.get("bis") ?? "") || null;
    const betrag = abweichend ? (fd.get("betrag") === "" ? null : Number(fd.get("betrag"))) : null;
    const grund = String(fd.get("grund") ?? "").trim();
    if (!kontakt) f.kontakt = "Für wen gilt der Beitrag?";
    if (!art) f.art = "Bitte eine Beitragsart wählen.";
    if (!von) f.von = "Ab wann gilt der Beitrag?";
    if (bis && von && bis < von) f.bis = "Das Ende liegt vor dem Beginn.";
    if (abweichend && (betrag === null || Number.isNaN(betrag) || betrag < 0)) f.betrag = "Bitte einen Betrag ab 0 € eintragen.";
    if (abweichend && !grund) f.grund = "Ein Sonderbetrag braucht einen Grund.";
    setFehler(f);
    if (Object.keys(f).length) return { error: Object.values(f)[0]!, field: Object.keys(f)[0] };

    const r = await zuordnungSpeichern(zuordnung?.id ?? null, {
      contactId: kontakt!.id,
      feeTypeId: art!.id,
      validFrom: von,
      validUntil: bis,
      paymentMethod: zahlart,
      amountOverride: abweichend ? betrag : null,
      grund: abweichend ? grund : null,
    });
    if (!r.success) {
      if (r.field) setFehler({ [r.field]: r.error });
      else toast.error(r.error);
      return r;
    }
    // Mit Sitzung zeichnet der Server die Liste neu; ohne baut der Dialog die Zeile selbst.
    const k = kontakt!;
    const a = art!;
    onGespeichert({
      id: zuordnung?.id ?? (r.id !== "cf-neu" ? r.id : `cf-neu-${++neueNummer}`),
      contactId: k.id,
      contactName: k.displayName,
      memberNumber: k.memberNumber,
      contactStatus: k.status,
      alter: zuordnung?.alter ?? null,
      householdId: haushalt?.id ?? null,
      householdName: haushalt?.name ?? null,
      payerId: zahler!.id,
      payerName: zahler!.displayName,
      feeTypeId: a.id,
      feeTypeName: a.name,
      interval: a.interval,
      baseAmount: a.amount,
      amountOverride: abweichend ? betrag : null,
      amount: abweichend && betrag !== null ? betrag : a.amount,
      validFrom: von,
      validUntil: bis,
      paymentMethod: zahlart,
      mandateId: zahlart === "SEPA" ? (mandat?.id ?? null) : null,
      mandateReference: zahlart === "SEPA" ? (mandat?.reference ?? null) : null,
      mandatsLage: zahlart === "SEPA" ? lage : null,
      note: abweichend ? grund : null,
      luecken:
        zahlart === "SEPA" && lage !== "gueltig"
          ? [{ art: "mandat", text: `Lastschrift, aber das Mandat ist ${lage === "fehlt" ? "nicht da" : lage}` }]
          : [],
    });
    return { success: true as const };
  }

  return (
    <Dialog open onClose={onClose}>
      <DialogForm action={speichern} refresh={false} success={zuordnung ? "Zuordnung gespeichert" : "Zuordnung angelegt"} onError={() => undefined}>
        <DialogHeader>
          <DialogTitle>{zuordnung ? `Beitrag von ${zuordnung.contactName}` : "Beitrag zuordnen"}</DialogTitle>
          <DialogDescription>Gilt ab dem nächsten Beitragslauf; gestellte Rechnungen bleiben, wie sie sind.</DialogDescription>
        </DialogHeader>
        <DialogContent>
          {!zuordnung && (
            <div className="space-y-1.5">
              <FieldLabel>Person</FieldLabel>
              <KontaktAuswahl value={kontaktId} onChange={setKontaktId} status={["ACTIVE", "PASSIVE", "HONORARY", "PROSPECT"]} kontakte={kontakte} />
              {fehler.kontakt && <p className="text-caption text-danger">{fehler.kontakt}</p>}
            </div>
          )}
          <Select
            label="Beitragsart"
            value={artId}
            onChange={setArtId}
            error={fehler.art}
            searchable={false}
            options={arten.map((a) => ({
              value: a.id,
              label: a.name,
              hint: `${a.amount === 0 ? "beitragsfrei" : euro(a.amount)} · ${FEE_INTERVAL[a.interval]}${a.perHousehold ? " · je Familie" : ""}`,
              disabled: !a.isActive && a.id !== zuordnung?.feeTypeId,
            }))}
          />
          {haushalt && (
            <DialogNotice tone="neutral">
              Zahlt über {haushalt.name}: Rechnung und Lastschrift gehen an {zahler?.displayName}.
            </DialogNotice>
          )}
          {wechsel && (
            <DialogNotice tone="neutral">
              Für einen Wechsel „Gilt ab“ auf den ersten Tag des neuen Beitrags setzen: die bisherige Zuordnung endet am Vortag und bleibt in der Historie. Bleibt das Datum, wird die Zuordnung von Anfang an berichtigt.
            </DialogNotice>
          )}
          <DialogRow>
            <Input name="von" type="date" label="Gilt ab" defaultValue={zuordnung?.validFrom ?? heuteIso()} error={fehler.von} />
            <Input name="bis" type="date" label="Gilt bis" defaultValue={zuordnung?.validUntil ?? ""} error={fehler.bis} hint="Leer: unbefristet" />
          </DialogRow>
          <Select
            label="Zahlart"
            value={zahlart}
            onChange={(v) => setZahlart(v as PaymentMethod)}
            searchable={false}
            options={[
              { value: "SEPA", label: "Lastschrift", hint: mandat ? `${mandat.reference} · ${mandat.ibanMasked}` : "Noch kein Mandat" },
              { value: "TRANSFER", label: "Überweisung" },
              { value: "CASH", label: "Bar" },
            ]}
          />
          {zahlart === "SEPA" && kontakt && lage !== "gueltig" && (
            <DialogNotice
              tone="warning"
              action={
                <Link href={`/beitraege/mandate?neu=${zahler?.id ?? ""}`} className="text-body-sm text-accent font-medium hover:underline">
                  Mandat erfassen
                </Link>
              }
            >
              {lage === "fehlt" ? "Für die Zahlperson gibt es kein Mandat." : `Das Mandat ist ${lage}.`} Der Beitragslauf stellt dann eine Überweisung.
            </DialogNotice>
          )}
          <DialogSection>
            <Switch checked={abweichend} onChange={setAbweichend} label="Abweichender Betrag" hint={art ? `Statt ${euro(art.amount)} laut Beitragsart` : undefined} />
            {abweichend && (
              <DialogRow template="9rem 1fr">
                <AmountInput name="betrag" label="Betrag (€)" defaultCents={zuordnung?.amountOverride !== null && zuordnung ? Math.round(zuordnung.amountOverride * 100) : null} error={fehler.betrag} className="tabular-nums" />
                <Input name="grund" label="Grund" defaultValue={zuordnung?.note ?? ""} placeholder="z. B. Ermäßigung Studium bis 09/2027" error={fehler.grund} />
              </DialogRow>
            )}
          </DialogSection>
        </DialogContent>
        <DialogFooter>
          <DialogCancel />
          <DialogSubmit>{zuordnung ? "Speichern" : "Zuordnen"}</DialogSubmit>
        </DialogFooter>
      </DialogForm>
    </Dialog>
  );
}
